import { XIcon } from "lucide-react";
import * as React from "react";
import { PopoverPortalContext } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

/**
 * Arkusz od dolu — jedyny wzorzec warstwy na telefonie (<md): filtry, „Więcej”,
 * menu „⋯”, sortowanie listy, szczegoly wiersza (`full`). Zbudowany na
 * `Sheet side="bottom"`, bez dodatkowych zaleznosci. Standard:
 * `.claude/skills/farmaster-ui-standard/references/mobile.md`.
 */
interface BottomSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: React.ReactNode;
	/** Krotki opis pod tytulem; bez niego Radix dostaje jawne `aria-describedby={undefined}`. */
	description?: React.ReactNode;
	children: React.ReactNode;
	/** Przyklejona stopka (np. „Wyczyść” + „Pokaż N …”). */
	footer?: React.ReactNode;
	/** Pelny ekran zamiast arkusza o wysokosci tresci. */
	full?: boolean;
	/** Zastepuje standardowy naglowek (tytul zostaje dostepny dla czytnikow). */
	header?: React.ReactNode;
	className?: string;
	bodyClassName?: string;
	"data-testid"?: string;
}

export function BottomSheet({
	open,
	onOpenChange,
	title,
	description,
	children,
	footer,
	full = false,
	header,
	className,
	bodyClassName,
	"data-testid": testId,
}: BottomSheetProps) {
	// Popovery (ScopePicker, comboboxy) portalowane do wnetrza arkusza — inaczej
	// focus-trap arkusza zabiera focus ich polom szukania.
	const [portalHost, setPortalHost] = React.useState<HTMLDivElement | null>(null);
	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="bottom"
				showCloseButton={false}
				data-testid={testId}
				{...(description ? {} : { "aria-describedby": undefined })}
				className={cn(
					"gap-0 bg-card text-card-foreground shadow-none",
					full ? "h-dvh max-h-dvh border-t-0" : "max-h-[92dvh] rounded-t-xl",
					className,
				)}
			>
				<PopoverPortalContext.Provider value={portalHost}>
					{header ? (
						<>
							<SheetTitle className="sr-only">{title}</SheetTitle>
							{header}
						</>
					) : (
						<>
							{!full && (
								<div aria-hidden className="mx-auto mt-2 mb-1 h-1 w-9 rounded-sm bg-border" />
							)}
							<div className="flex shrink-0 items-center justify-between gap-2 px-3.5 pt-1 pb-2">
								<div className="min-w-0">
									<SheetTitle className="truncate text-base">{title}</SheetTitle>
									{description && (
										<SheetDescription className="text-xs">{description}</SheetDescription>
									)}
								</div>
								<button
									type="button"
									onClick={() => onOpenChange(false)}
									aria-label="Zamknij"
									className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
								>
									<XIcon className="size-4" />
								</button>
							</div>
						</>
					)}
					<div className={cn("min-h-0 flex-1 overflow-y-auto px-3.5 pb-3", bodyClassName)}>
						{children}
					</div>
					{footer && (
						<div className="flex shrink-0 gap-2 border-t border-border px-3.5 pt-2.5 pb-[max(0.875rem,env(safe-area-inset-bottom))]">
							{footer}
						</div>
					)}
				</PopoverPortalContext.Provider>
				<div ref={setPortalHost} data-slot="bottom-sheet-portal-host" />
			</SheetContent>
		</Sheet>
	);
}

/**
 * Stopka arkusza filtrow: „Wyczyść” (drugorzedny) + akcja glowna 44 px, np.
 * „Pokaż 128 wierszy”. Filtry dzialaja na zywo, wiec akcja glowna tylko zamyka.
 */
export function SheetFooterActions({
	onClear,
	clearLabel = "Wyczyść",
	clearDisabled = false,
	primaryLabel,
	onPrimary,
	primaryDisabled = false,
}: {
	onClear?: () => void;
	clearLabel?: string;
	clearDisabled?: boolean;
	primaryLabel: React.ReactNode;
	onPrimary: () => void;
	/** Akcja glowna wymaga wyboru (np. strategii) zanim cos zrobi. */
	primaryDisabled?: boolean;
}) {
	return (
		<>
			{onClear && (
				<button
					type="button"
					onClick={onClear}
					disabled={clearDisabled}
					data-testid="sheet-clear"
					className="h-11 flex-1 rounded-md border border-border bg-card text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
				>
					{clearLabel}
				</button>
			)}
			<button
				type="button"
				onClick={onPrimary}
				disabled={primaryDisabled}
				data-testid="sheet-apply"
				className="h-11 flex-[2] truncate rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
			>
				{primaryLabel}
			</button>
		</>
	);
}

/** Pozycja listy w arkuszu (menu „⋯”, „Więcej”, sortowanie): wiersz 44 px. */
export function SheetRow({
	icon,
	children,
	active = false,
	className,
	...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: React.ReactNode; active?: boolean }) {
	return (
		<button
			type="button"
			className={cn(
				"flex min-h-11 w-full items-center gap-3 rounded-md px-2 text-left text-sm transition-colors",
				"hover:bg-accent disabled:pointer-events-none disabled:opacity-50",
				active && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
				className,
			)}
			{...props}
		>
			{icon && <span className="flex size-5 shrink-0 items-center justify-center">{icon}</span>}
			<span className="min-w-0 flex-1">{children}</span>
		</button>
	);
}
